"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageTitle } from "@/lib/usePageTitle";
import { type SubscriptionStatus, type SubscriptionTier } from "@/lib/billing/plans";

function tierLabel(tier: SubscriptionTier) {
  if (tier === "free") return "Free";
  return `Founder ${tier.charAt(0).toUpperCase()}${tier.slice(1)}`;
}

function statusLabel(status: SubscriptionStatus) {
  if (status === "none") return "No active subscription";
  return status.replace(/_/g, " ");
}

function requestedPlanLabel(plan: string | null) {
  if (!plan) return null;
  if (plan === "starter") return "Founder Starter";
  if (plan === "plus") return "Founder Plus";
  if (plan === "pro") return "Founder Pro";
  if (plan === "basic") return "Basic";
  if (plan === "enterprise") return "Enterprise";
  return null;
}

export default function PaymentCancelPage() {
  usePageTitle("Payment Canceled");

  const searchParams = useSearchParams();
  const summary = useQuery(api.billing.getMySummary);
  const requestedPlan = requestedPlanLabel(searchParams.get("plan"));

  const tier = (summary?.tier ?? "free") as SubscriptionTier;
  const status = (summary?.status ?? "none") as SubscriptionStatus;
  const hasPaidPlan =
    tier !== "free" && (status === "active" || status === "trialing");

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-4 py-8">
      {/* Cancellation notice */}
      <Card className="border-amber-300 bg-amber-50/70 dark:border-amber-700 dark:bg-amber-950/20">
        <CardHeader>
          <CardTitle>Payment Canceled</CardTitle>
          <CardDescription>
            You left checkout before completing your payment. No charge was
            made.
          </CardDescription>
        </CardHeader>
        {requestedPlan && (
          <CardContent className="pt-0 text-sm text-muted">
            Selected plan:{" "}
            <span className="font-medium text-foreground">{requestedPlan}</span>
          </CardContent>
        )}
      </Card>

      {/* Current subscription status */}
      {summary === undefined ? (
        <Card>
          <CardHeader>
            <CardTitle>Loading Current Plan</CardTitle>
            <CardDescription>
              Checking your current subscription status.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Skeleton className="h-4 w-44" />
            <Skeleton className="h-4 w-64" />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="space-y-2">
            <div className="flex items-center gap-2">
              <CardTitle>Current Subscription</CardTitle>
              <Badge variant={hasPaidPlan ? "success" : "secondary"}>
                {hasPaidPlan ? "Active" : "No Active Plan"}
              </Badge>
            </div>
            <CardDescription>
              {hasPaidPlan
                ? "Your existing subscription remains unchanged."
                : "Ready to try again? Pick a plan below and complete checkout."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-1 text-sm text-muted">
            <p>
              Plan:{" "}
              <span className="font-medium text-foreground">
                {tierLabel(tier)}
              </span>
            </p>
            <p>
              Status:{" "}
              <span className="font-medium capitalize text-foreground">
                {statusLabel(status)}
              </span>
            </p>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Link href="/pricing">
          <Button>Choose a Plan</Button>
        </Link>
        <Link href="/dashboard">
          <Button variant="outline">Back to Dashboard</Button>
        </Link>
      </div>
    </div>
  );
}
